/**
 * Single source of truth for the page-level disclaimer footnotes shown at the
 * bottom of the Analysis page.
 *
 * Design (see the session where this was introduced): disclaimers used to live
 * inline as Highcharts `caption`s directly under the charts they caveated. That
 * put dense methodology text in the chart frame itself. Instead we collect them
 * into one numbered footer at the bottom of the page (`DisclaimerFooter`), and
 * each chart carries only a small superscript marker (`¹`, `²`, …) next to its
 * title, pointing at the matching footnote.
 *
 * The numbering is STATIC and stable: every footnote is always listed, in this
 * fixed order, regardless of which metric/period the user currently has
 * selected. The marker on a chart is likewise always present. This is the
 * conventional financial-disclaimer-footer pattern — the footnote text is
 * written to be self-scoping ("Where shown, …"), so it reads correctly even
 * when the specific caveat doesn't apply to the current selection (e.g. the
 * implied-estimate note when EPS, which has real consensus, is selected). This
 * deliberately trades a little precision for stable numbering and far simpler
 * wiring than making markers/footnotes appear and renumber dynamically.
 *
 * To add a disclaimer: append an entry here (never reorder or renumber existing
 * ones — a marker superscript in a component references an entry by its `id`,
 * and its displayed number is this array's 1-based index). Reference it from a
 * chart via `<DisclaimerMarker id="..." />` (see DisclaimerMarker.js).
 */

/**
 * @typedef {Object} Disclaimer
 * @property {string} id     Stable key a chart marker references (never change).
 * @property {string} short  A few words naming the topic, for the marker's
 *                           accessible label / tooltip.
 * @property {string} text   The full footnote text shown in the footer.
 */

/** @type {Disclaimer[]} */
export const DISCLAIMERS = [
  {
    // Free-cash-flow sentence is the backend contract in funviz-amplify
    // (ADR 0001, 2026-11-02b): forward FCF = five-year median FCF/EBITDA x
    // EBITDA consensus, the same model the screener uses. There is no
    // divergence gate (the old hold/skip was removed). A negative forward FCF
    // year is left out. Operating cash flow stays on the five-year median.
    // Dividends are no longer derived from a payout ratio — see the
    // dividend forecast footnote below (funviz-amplify ADR 0013).
    id: 'implied-estimates',
    short: 'Implied forward estimates',
    text:
      'Implied forward estimates. Free cash flow and operating cash flow use analyst '
      + 'EBITDA consensus × the five-year median ratio of that cash-flow metric to EBITDA. '
      + 'A forward free-cash-flow year that comes out negative is left out. These are '
      + 'heuristics, not analyst forecasts—no high/low range. Dividends are projected from the '
      + 'company’s own dividend history instead (see the dividend forecast note).',
  },
  {
    id: 'ntm-realized',
    short: 'Backtest uses realized values',
    text:
      'Valuation-vs-return backtest. For every past date on the “Valuation vs. Subsequent '
      + 'Returns” plot, the forward (NTM) multiple uses the realized (actual) values that '
      + 'followed — we do not have point-in-time historical analyst consensus and do not '
      + 'attempt to reconstruct it. Only the current-date point reflects live analyst '
      + 'consensus. The trailing (TTM) option uses fully realized values for every point, '
      + 'including the current one, with no estimates involved.',
  },
  {
    // The id is kept (ids and positions are stable — see the header) but the
    // entry now describes the dividend forecast itself; the old payout-ratio
    // "profile deviation" override it described no longer exists
    // (funviz-amplify ADR 0013). Same model as the Screener's forward and
    // blended dividend yields.
    id: 'dividend-profile-deviation',
    short: 'Dividend forecast',
    text:
      'Dividend forecast. Dividends are shown as regular dividends only; special dividends '
      + 'appear on hover and are left out of the forecast. The forecast starts from the regular '
      + 'dividends paid over the last twelve months and grows them at the median of the last five '
      + 'years’ year-over-year growth, capped at ±10% a year (held flat with fewer than three years '
      + 'of growth history). A forecast year is held flat when analysts expect at least two of '
      + 'EPS, revenue and EBITDA to fall more than 25% below their five-year median. A company '
      + 'that paid nothing regular in the last twelve months gets no forecast. This is a '
      + 'heuristic, not an analyst forecast.',
  },
  {
    id: 'income-statement-sankey',
    short: 'Income flow',
    text:
      'Income flow. The Sankey breaks one reporting period down from revenue through '
      + 'costs and expenses to net income, splitting a node into sub-line-items (e.g. R&D, SG&A, '
      + 'D&A) only where the data provider reports them and collapsing that split otherwise. A '
      + 'Sankey flow cannot be negative, so a loss period (negative gross profit, operating loss, '
      + 'or net loss) is annotated with a note and its loss node shown in purple with the true '
      + '(negative) figure in the label, while the drawn ribbon uses the magnitude. A loss node is '
      + 'a thin pass-through so the flow still continues to the terminal figures; its bar is drawn '
      + 'at a fixed minimum height (marked ↕) rather than a true proportional one. Figures are '
      + 'shown in the company’s filing (reporting) currency, which may differ from the '
      + 'currency-converted figures shown elsewhere on this page.',
  },
  {
    id: 'quality-score',
    short: 'Quality score and Rule of 40',
    text:
      'Quality score and Rule of 40. Quality is a 0–100 house score for high-quality '
      + 'compounders that generate cash, do not dilute and carry modest debt. Its six '
      + 'parts are profitability, dilution, debt coverage, free-cash-flow quality, '
      + 'revenue quality and consistency (rising EPS, steady margins; '
      + 'weighted most); they add up to more than 100. Free-cash-flow '
      + 'quality is after stock-based compensation; revenue or free cash flow that has '
      + 'swung sharply is marked down. The plotted line is a trailing three-year average. '
      + 'Profitability averages net margin with operating ROA. Financials (bar '
      + 'goodwill-heavy ones) use equity to assets (debt), ROA (profitability) and net '
      + 'income (cash, capped at 30 of 40, and revenue), no consistency part; REITs use '
      + 'net debt to EBITDA. '
      + 'Quality measures '
      + 'backward-looking accounting stability; banks\' and insurers\' main risks '
      + '(leverage, liquidity, tail events) are not fully visible '
      + 'in it. The value-basis, per-share/total and “Adjust for SBC” switches do not '
      + 'move it. Growth is a Rule-of-40-style score: revenue growth (up to 100%) '
      + 'plus three-quarters of the EBITDA margin (operating cash flow for REITs; −100% to '
      + '40%), where 70% scores 100, on total and per-share revenue (total ≤ per-share '
      + 'growth). Only growth past the 5-year revenue peak counts, less if >20% of up '
      + 'to 10 years fell; under 15% growth a year is capped (5% → 33, 10% → 67), revenue ≤ 0 '
      + 'scores 0, and the line is a three-year average (≤ 30 after revenue ≤ 0). '
      + '“Estimated” years use analyst revenue and the last reported margin. No estimate '
      + 'enters the quality score. Neither line is a rating or recommendation.',
  },
];

/** Look up a disclaimer and its 1-based footnote number by id. Returns `null`
 *  for an unknown id so a mis-typed marker fails loudly-ish (no marker rendered)
 *  rather than pointing at the wrong footnote. */
export const getDisclaimer = (id) => {
  const index = DISCLAIMERS.findIndex((d) => d.id === id);
  return index === -1 ? null : { ...DISCLAIMERS[index], number: index + 1 };
};
